/**
 * ApplyTemplateModalCN — shared modal used by every evaluation workspace tab
 * (Scoring, Financials × 3, Risk, Pros/Cons) to apply a saved template across
 * the evaluation's sites.
 *
 * Self-contained: fetches the template list and per-template item preview on
 * its own. The host workspace passes `templateType` + `evalId` and refreshes
 * its data in `onApplied`.
 *
 * Linear: MYD-318 (foundation). Endpoint URLs are mapped from `templateType`
 * via the tables at the top of this file. They will 404 until each category's
 * backend issue lands (scoring → MYD-319, financials → MYD-322, risk →
 * MYD-329, pros/cons → MYD-332). The modal surfaces those errors verbatim.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import axios from 'axios'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Badge } from 'components/ui/badge'
import { Checkbox } from 'components/ui/checkbox'
import {
	BookOpenCheck,
	LayoutTemplate,
	Loader2,
	AlertTriangle,
	CheckCircle2,
} from 'lucide-react'
import { cn } from 'lib/utils'
import type {
	ApplyTemplateModalProps,
	EvaluationTemplateType,
	FinancialModelType,
	TemplateSummary,
} from './types'

// ---------------------------------------------------------------------------
// URL maps. Adding a new templateType requires entries here AND in types.ts.
// ---------------------------------------------------------------------------

const APPLY_URL: Record<EvaluationTemplateType, (evalId: string) => string> = {
	scoring: (evalId) => `/api/evaluations/${evalId}/apply-scoring-template`,
	acquisition: (evalId) => `/api/evaluations/${evalId}/apply-financial-template`,
	operating: (evalId) => `/api/evaluations/${evalId}/apply-financial-template`,
	roi: (evalId) => `/api/evaluations/${evalId}/apply-financial-template`,
	risk: (evalId) => `/api/evaluations/${evalId}/apply-risk-template`,
	prosCons: (evalId) => `/api/evaluations/${evalId}/apply-pros-cons-template`,
}

const CHILD_ITEMS_URL: Record<EvaluationTemplateType, (templateId: string) => string> = {
	scoring: (id) => `/api/templates/${id}/scoringCriterion`,
	acquisition: (id) => `/api/templates/${id}/financialLineItem`,
	operating: (id) => `/api/templates/${id}/financialLineItem`,
	roi: (id) => `/api/templates/${id}/financialLineItem`,
	risk: (id) => `/api/templates/${id}/risk`,
	prosCons: (id) => `/api/templates/${id}/proCon`,
}

const TYPE_LABEL: Record<EvaluationTemplateType, string> = {
	scoring: 'scoring',
	acquisition: 'acquisition',
	operating: 'operating',
	roi: 'ROI analysis',
	risk: 'risk',
	prosCons: 'pros & cons',
}

// What text to use for an item row in the preview pane. The shape of each
// item varies by templateType — the modal doesn't enforce it, just picks the
// most useful fields if present.
function describeItem(
	type: EvaluationTemplateType,
	item: any,
): { primary: string; secondary?: string } {
	if (type === 'scoring') {
		return { primary: item.name, secondary: item.category ? `${item.category} · weight ${item.weight}` : `weight ${item.weight}` }
	}
	if (type === 'acquisition' || type === 'operating' || type === 'roi') {
		const parts: string[] = []
		if (item.category) parts.push(item.category)
		if (item.defaultAmount !== undefined && item.defaultAmount !== null)
			parts.push(formatCurrency(item.defaultAmount))
		if (item.escalationRate) parts.push(`esc ${Math.round(item.escalationRate * 100)}%`)
		if (item.year !== undefined && item.year !== null) parts.push(`Y${item.year}`)
		return { primary: item.label, secondary: parts.join(' · ') || undefined }
	}
	if (type === 'risk') {
		return { primary: item.name, secondary: `${item.category} · L${item.defaultLikelihood}/I${item.defaultImpact}` }
	}
	// prosCons
	return { primary: item.description, secondary: `${item.type} · ${item.category}` }
}

function formatCurrency(n: number): string {
	if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(0)}k`
	return `$${n}`
}

/**
 * What the apply endpoints report back. The financial, risk, and pros/cons
 * endpoints all return per-site counts; scoring returns a different shape, so
 * `sites` is optional and the panel degrades to a plain acknowledgement.
 */
interface ApplyStats {
	applied: number
	replaced: number
	skipped: number
}

interface ApplyResult {
	templateName: string
	sites?: ApplyStats
}

const pluralSites = (n: number) => `${n} site${n === 1 ? '' : 's'}`

// ---------------------------------------------------------------------------

const ApplyTemplateModalCN = ({
	open,
	onOpenChange,
	templateType,
	evalId,
	applyOptions,
	onApplied,
}: ApplyTemplateModalProps) => {
	const token = useSelector((s: any) => s?.userLogin?.userInfo?.token)
	const authConfig = useMemo(
		() => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
		[token],
	)

	const [templates, setTemplates] = useState<TemplateSummary[]>([])
	const [templatesLoading, setTemplatesLoading] = useState(false)
	const [templatesError, setTemplatesError] = useState<string | null>(null)

	const [selectedId, setSelectedId] = useState<string | null>(null)
	const [previewItems, setPreviewItems] = useState<any[]>([])
	const [previewLoading, setPreviewLoading] = useState(false)
	const [previewError, setPreviewError] = useState<string | null>(null)

	const [overwrite, setOverwrite] = useState(false)
	const [applying, setApplying] = useState(false)
	const [applyError, setApplyError] = useState<string | null>(null)
	const [applyResult, setApplyResult] = useState<ApplyResult | null>(null)

	// Fetch the template list when the modal opens or templateType changes.
	useEffect(() => {
		if (!open) return
		let cancelled = false
		setTemplatesLoading(true)
		setTemplatesError(null)
		setSelectedId(null)
		setPreviewItems([])
		setOverwrite(false)
		setApplyError(null)
		setApplyResult(null)
		axios
			.get<TemplateSummary[]>('/api/templates', authConfig)
			.then((res) => {
				if (cancelled) return
				const filtered = (res.data || [])
					.filter((t) => t.templateType === templateType)
					.sort((a, b) => {
						// Public templates first, then alphabetical.
						const ap = a.accessLevel === 'public' ? 0 : 1
						const bp = b.accessLevel === 'public' ? 0 : 1
						if (ap !== bp) return ap - bp
						return a.templateName.localeCompare(b.templateName)
					})
				setTemplates(filtered)
			})
			.catch((err) => {
				if (cancelled) return
				setTemplatesError(
					err?.response?.data?.message || err.message || 'Failed to load templates',
				)
			})
			.finally(() => {
				if (cancelled) return
				setTemplatesLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [open, templateType, authConfig])

	// Fetch preview items when a template is selected.
	useEffect(() => {
		if (!selectedId) {
			setPreviewItems([])
			setPreviewError(null)
			return
		}
		let cancelled = false
		setPreviewLoading(true)
		setPreviewError(null)
		axios
			.get(CHILD_ITEMS_URL[templateType](selectedId), authConfig)
			.then((res) => {
				if (cancelled) return
				setPreviewItems(Array.isArray(res.data) ? res.data : [])
			})
			.catch((err) => {
				if (cancelled) return
				setPreviewItems([])
				setPreviewError(
					err?.response?.status === 404
						? 'Preview endpoint not yet wired for this template type.'
						: err?.response?.data?.message ||
							err.message ||
							'Failed to load template preview',
				)
			})
			.finally(() => {
				if (cancelled) return
				setPreviewLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [selectedId, templateType, authConfig])

	const selected = templates.find((t) => t._id === selectedId) || null
	const itemCount = previewItems.length

	// `useOverwrite` is passed explicitly rather than read from state so the
	// "Overwrite and re-apply" retry on the result panel can force it without
	// waiting for a state update to land.
	const handleApply = useCallback(
		async (useOverwrite: boolean) => {
		if (!selectedId || !selected) return

		if (useOverwrite) {
			const ok = await toast.confirm(
				`Overwrite existing ${TYPE_LABEL[templateType]} data on every site in this evaluation? This is destructive and cannot be undone.`,
			)
			if (!ok) return
		}

		setApplying(true)
		setApplyError(null)
		setApplyResult(null)
		try {
			const modelType: FinancialModelType | undefined =
				applyOptions?.modelType ??
				(templateType === 'acquisition' || templateType === 'operating' || templateType === 'roi'
					? (templateType as FinancialModelType)
					: undefined)
			const { data } = await axios.post(
				APPLY_URL[templateType](evalId),
				{
					templateId: selectedId,
					overwrite: useOverwrite,
					applyTo: 'all',
					...(modelType ? { modelType } : {}),
				},
				authConfig,
			)
			// Report what the server actually did rather than closing blind — a
			// merge that skipped every site returns 201 just like a real apply,
			// and used to be indistinguishable from "nothing happened".
			setApplyResult({
				templateName: data?.templateName || selected.templateName,
				sites: data?.sites,
			})
			onApplied?.()
		} catch (err: any) {
			setApplyError(
				err?.response?.data?.message || err?.message || 'Failed to apply template',
			)
		} finally {
			setApplying(false)
		}
		},
		[selectedId, selected, applyOptions, templateType, evalId, authConfig, onApplied],
	)

	// A merge that touched nothing — every site already had data and the user
	// left Overwrite unchecked. Worth calling out explicitly, since it's the
	// case that reads as "the button did nothing".
	const noChanges =
		!!applyResult?.sites &&
		applyResult.sites.applied === 0 &&
		applyResult.sites.replaced === 0 &&
		applyResult.sites.skipped > 0

	return (
		<Dialog open={open} onClose={() => onOpenChange(false)}>
			<DialogContent className="max-w-2xl flex flex-col max-h-[85vh]">
				<DialogHeader>
					<div className="min-w-0">
						<DialogTitle className="flex items-center gap-2">
							<LayoutTemplate className="w-5 h-5 text-[#0080fe]" />
							Apply {TYPE_LABEL[templateType]} template
						</DialogTitle>
						<DialogDescription>
							Seeds every site in this evaluation with the template's items.
						</DialogDescription>
					</div>
				</DialogHeader>

				<div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3">
					{applyResult ? (
						<ApplyResultPanel
							result={applyResult}
							typeLabel={TYPE_LABEL[templateType]}
							noChanges={noChanges}
						/>
					) : templatesLoading ? (
						<div className="text-center py-8">
							<Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" />
							<p className="mt-2 text-xs text-gray-500">Loading templates…</p>
						</div>
					) : templatesError ? (
						<div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
							<AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
							<span>{templatesError}</span>
						</div>
					) : templates.length === 0 ? (
						<EmptyState
							title={`No ${TYPE_LABEL[templateType]} templates yet`}
							hint="Save your current items as a template, or ask an admin to add one in Admin > Templates."
						/>
					) : (
						<>
							<div className="space-y-1.5 max-h-[260px] overflow-y-auto">
								{templates.map((t) => {
									const isSelected = t._id === selectedId
									return (
										<button
											key={t._id}
											type="button"
											onClick={() => setSelectedId(t._id)}
											className={cn(
												'w-full text-left px-3 py-2.5 rounded-md border transition-colors',
												isSelected
													? 'border-[#0080fe] bg-[#0080fe]/5'
													: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
											)}
										>
											<div className="flex items-center justify-between gap-2">
												<div className="flex items-center gap-2 min-w-0">
													<span className="font-medium text-sm text-gray-900 truncate">
														{t.templateName}
													</span>
													{t.accessLevel === 'public' && (
														<Badge className="text-[10px] bg-[#0080fe]/10 text-[#0080fe]">
															Public
														</Badge>
													)}
													{t.accessLevel === 'org-wide' && (
														<Badge className="text-[10px] bg-indigo-100 text-indigo-700">
															Org
														</Badge>
													)}
												</div>
											</div>
											{t.description && (
												<p className="mt-1 text-xs text-gray-500 line-clamp-2">
													{t.description}
												</p>
											)}
										</button>
									)
								})}
							</div>

							{selected && (
								<div className="rounded-md bg-gray-50 px-3 py-2.5 max-h-[200px] overflow-y-auto">
									<div className="flex items-center justify-between mb-1.5">
										<div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
											Preview
										</div>
										{!previewLoading && !previewError && (
											<Badge className="text-[10px] bg-gray-200 text-gray-700">
												{itemCount} item{itemCount === 1 ? '' : 's'}
											</Badge>
										)}
									</div>
									{previewLoading ? (
										<div className="flex items-center gap-2 text-xs text-gray-500">
											<Loader2 className="w-3.5 h-3.5 animate-spin" />
											Loading preview…
										</div>
									) : previewError ? (
										<p className="text-xs text-amber-700">{previewError}</p>
									) : itemCount === 0 ? (
										<p className="text-xs text-gray-500">
											This template has no items yet.
										</p>
									) : (
										<div className="space-y-1">
											{previewItems.slice(0, 10).map((item: any, idx: number) => {
												const { primary, secondary } = describeItem(templateType, item)
												return (
													<div
														key={item._id || idx}
														className="flex items-center justify-between text-xs gap-3"
													>
														<span className="text-gray-700 truncate">{primary}</span>
														{secondary && (
															<span className="text-gray-400 shrink-0">{secondary}</span>
														)}
													</div>
												)
											})}
											{itemCount > 10 && (
												<p className="text-[11px] text-gray-400 pt-1">
													…and {itemCount - 10} more
												</p>
											)}
										</div>
									)}
								</div>
							)}

							<label className="flex items-start gap-2 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 cursor-pointer">
								<Checkbox
									checked={overwrite}
									onCheckedChange={(v) => setOverwrite(v === true)}
									className="mt-0.5"
								/>
								<span className="text-xs text-amber-800">
									<strong>Overwrite existing</strong> {TYPE_LABEL[templateType]} data on
									every site. Without this, the template merges into what's already there
									(or refuses if the server detects a conflict).
								</span>
							</label>

							{applyError && (
								<div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
									<AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
									<span>{applyError}</span>
								</div>
							)}
						</>
					)}
				</div>

				<DialogFooter className="px-5 pb-4 pt-2 gap-2">
					{applyResult ? (
						<>
							{noChanges && (
								<Button
									variant="primary"
									isOutline
									onClick={() => handleApply(true)}
									disabled={applying}
									className="gap-1.5"
								>
									<BookOpenCheck className="w-4 h-4" />
									Overwrite and re-apply
								</Button>
							)}
							<Button variant="primary" onClick={() => onOpenChange(false)}>
								Done
							</Button>
						</>
					) : (
						<>
							<Button
								variant="ghost"
								onClick={() => onOpenChange(false)}
								disabled={applying}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								onClick={() => handleApply(overwrite)}
								disabled={!selectedId || applying}
								className="gap-1.5"
							>
								{applying ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										Applying…
									</>
								) : (
									<>
										<BookOpenCheck className="w-4 h-4" />
										Apply template
									</>
								)}
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

/**
 * Post-apply summary. The whole point is that "applied to 4 sites" and
 * "skipped all 4 sites" no longer look the same from the outside.
 */
const ApplyResultPanel = ({
	result,
	typeLabel,
	noChanges,
}: {
	result: ApplyResult
	typeLabel: string
	noChanges: boolean
}) => {
	const stats = result.sites
	const changed = stats ? stats.applied + stats.replaced : null

	if (noChanges && stats) {
		return (
			<div className="py-4 space-y-3">
				<div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
					<AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
					<div className="space-y-1">
						<p className="font-medium">Nothing changed</p>
						<p className="text-xs leading-relaxed">
							Every site ({pluralSites(stats.skipped)}) already has {typeLabel} data, and
							Overwrite was off, so <strong>{result.templateName}</strong> was skipped
							everywhere. Re-apply with overwrite to replace what's there.
						</p>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="py-4 space-y-3">
			<div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-green-50 border border-green-200 text-green-800 text-sm">
				<CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
				<div className="space-y-1">
					<p className="font-medium">
						Applied {result.templateName}
						{changed != null && ` to ${pluralSites(changed)}`}
					</p>
					{stats && (
						<p className="text-xs leading-relaxed">
							{stats.applied} seeded · {stats.replaced} replaced · {stats.skipped} skipped
							{stats.skipped > 0 && ' (already had data)'}
						</p>
					)}
				</div>
			</div>
		</div>
	)
}

const EmptyState = ({ title, hint }: { title: string; hint: string }) => (
	<div className="text-center py-8">
		<LayoutTemplate className="w-10 h-10 text-gray-300 mx-auto" />
		<p className="text-sm text-gray-700 font-medium mt-2">{title}</p>
		<p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">{hint}</p>
	</div>
)

export default ApplyTemplateModalCN
